/**
 * Domain types, constants, and factory functions for the Government Intake form.
 * Extracted from context to satisfy single-responsibility: this file owns type definitions only.
 */

// JSON-serializable file data type
export interface FileData {
  name: string;
  size: number;
  type: string;
  data: string; // Base64 encoded content
}

// ───────────────────────── Entry & Context ─────────────────────────

export const EMPTY_ENTRY_CONTACT = {
  cityOrAgencyName: '',
  organizationType: '',
  state: '',
  department: '',
  fullName: '',
  jobTitle: '',
  email: '',
  phone: '',
  preferredContact: '',
};

export const EMPTY_ROLE_CONTEXT = {
  roleDescription: '',
  involvedInApprovalRecommendation: '',
  fromSeinTerminalPerspective: '',
  successAfterTwelveMonth: '',
};

export type EntryContactInfo = typeof EMPTY_ENTRY_CONTACT;
export type RoleContext = typeof EMPTY_ROLE_CONTEXT;

// ───────────────────────── Current Systems ─────────────────────────

export const EMPTY_NON_SYSTEM_TOOLS = {
  email: false,
  spreadsheet: false,
  drives: false,
  custom: false,
};

export const EMPTY_CURRENT_SYSTEMS = {
  systems: [] as SystemData[],
  nonSystemTools: EMPTY_NON_SYSTEM_TOOLS,
};

export type SystemData = {
  id: number;
  name: string;
  vendor: string;
  note: string;
  missionCritical?: boolean;
};

export type NonSystemTools = typeof EMPTY_NON_SYSTEM_TOOLS;

export type CurrentSystemsData = {
  systems: SystemData[];
  nonSystemTools: NonSystemTools;
};

export type HowItIsUsed = {
  systemOfRecord: boolean;
  referenceOnly: boolean;
  reporting: boolean;
  intake: boolean;
  storage: boolean;
};

export type DataAccessAvailable = {
  screen: boolean;
  export: boolean;
  api: boolean;
  unsure: boolean;
};

// ───────────────────────── Programs ─────────────────────────

export interface ProgramSubScopingData {
  anticipatedPrograms: string;
  automatedReporting: string;
  reportCount: string;
  automatedReportingFile: FileData[];
  programForms: string;
  formCount: string;
  automatedFormFile: FileData[];
  consolidate: string;
  dataLocations: string[];
  numberOfProperties: string;
  approxParticipants: string;
  approxDocumentsFiles: string;
  dataQuality: string;
}

export interface ProgramSystem {
  id: string;
  name: string;
  vendor?: string;
  notes?: string;
}

export interface ProgramCurrentSystemsData {
  systems: ProgramSystem[];
}

export const EMPTY_PROGRAM_CURRENT_SYSTEMS: ProgramCurrentSystemsData = {
  systems: [],
};

export interface ProgramStructureData {
  programTypes: Record<string, boolean>;
  programTracking: Record<string, boolean>;
  programTiedTo: Record<string, boolean>;
}

export const EMPTY_PROGRAM_STRUCTURE: ProgramStructureData = {
  programTypes: {},
  programTracking: {},
  programTiedTo: {},
};

export interface ProgramPainPointsData {
  biggestBurden: string;
  delaysAndErrors: string;
  hardestToProducе: string;
  painPoint1: string;
  painPoint2: string;
  painPoint3: string;
  successIn6To12Months: string;
}

export const EMPTY_PROGRAM_PAIN_POINTS: ProgramPainPointsData = {
  biggestBurden: '',
  delaysAndErrors: '',
  hardestToProducе: '',
  painPoint1: '',
  painPoint2: '',
  painPoint3: '',
  successIn6To12Months: '',
};

export type ProgramSubSections = {
  scoping: ProgramSubScopingData;
  currentSystems: ProgramCurrentSystemsData;
  structure: ProgramStructureData;
  painPoints: ProgramPainPointsData;
};

// ───────────────────────── Projects ─────────────────────────

export interface ProjectsSubScopingData {
  anticipatedProjects: string;
  automatedReporting: string;
  reportCount: string;
  automatedReportingFile: FileData[];
  programForms: string;
  formCount: string;
  automatedFormFile: FileData[];
  consolidate: string;
  dataLocations: string[];
  numberOfProperties: string;
  approxParticipants: string;
  approxDocumentsFiles: string;
  dataQuality: string;
}

export interface ProjectsStructureData {
  deptInvolved: string;
  programsTrackedToday: string;
  visibilityGaps: string;
  leadershipRequests: string;
}

export const EMPTY_PROJECTS_STRUCTURE_DATA: ProjectsStructureData = {
  deptInvolved: '',
  programsTrackedToday: '',
  visibilityGaps: '',
  leadershipRequests: '',
};

export interface ProjectsPainPointsData {
  deptInvolved: string;
  externalPartnersInvolved: string;
  visibilityGaps: string;
  painPoint1: string;
  painPoint2: string;
  painPoint3: string;
  successIn6To12Months: string;
}

export const EMPTY_PROJECTS_PAIN_POINTS: ProjectsPainPointsData = {
  deptInvolved: '',
  externalPartnersInvolved: '',
  visibilityGaps: '',
  painPoint1: '',
  painPoint2: '',
  painPoint3: '',
  successIn6To12Months: '',
};

export type ProjectsSubSections = {
  scoping: ProjectsSubScopingData;
  currentSystems: ProgramCurrentSystemsData;
  structure: ProjectsStructureData;
  painPoints: ProjectsPainPointsData;
};

// ───────────────────────── Properties ─────────────────────────

export interface PropertyTrackingData {
  propertyType: Record<string, boolean>;
  propertyTracking: Record<string, boolean>;
  propertyUpdates: Record<string, boolean>;
  visibilityGaps: string;
}

export const EMPTY_PROJECT_TRACKING_DATA: PropertyTrackingData = {
  propertyType: {},
  propertyTracking: {},
  propertyUpdates: {},
  visibilityGaps: '',
};

export interface PropertyLegacyConsolidationData {
  consolidate: string;
  propertyDataLive: Record<string, boolean>;
  numberOfProperties: string;
  approxParticipants: string;
  approxDocumentsFiles: string;
  dataQuality: string;
}

export interface PropertiesSubNeedsData {
  automatedReporting: string;
  reportCount: string;
  automatedReportingFile: FileData[];
  programForms: string;
  formCount: string;
  automatedFormFile: FileData[];
}

export interface PropertiesPainPointsData {
  painPoint1: string;
  painPoint2: string;
  painPoint3: string;
  leadershipAsksFor: string;
}

export const EMPTY_PROPERTIES_PAIN_POINTS: PropertiesPainPointsData = {
  painPoint1: '',
  painPoint2: '',
  painPoint3: '',
  leadershipAsksFor: '',
};

export type PropertiesSubSections = {
  tracking: PropertyTrackingData;
  operationalSystems: ProgramCurrentSystemsData;
  legacyConsolidation: PropertyLegacyConsolidationData;
  needs: PropertiesSubNeedsData;
  painPoints: PropertiesPainPointsData;
};

// ───────────────────────── Primary Object of Work ─────────────────────────

export type PrimaryObjectOfWorkSectionKey = 'programs' | 'projects' | 'properties';

export type PrimaryObjectOfWorkItem =
  | { programs: ProgramSubSections }
  | { projects: ProjectsSubSections }
  | { properties: PropertiesSubSections };

// ───────────────────────── Root Payload ─────────────────────────

export type IntakePayload = {
  entryContact: EntryContactInfo;
  roleContext: RoleContext;
  currentSystems: CurrentSystemsData;
  primaryObjectOfWork: PrimaryObjectOfWorkItem[];
};

export const EMPTY_INTAKE_PAYLOAD: IntakePayload = {
  entryContact: EMPTY_ENTRY_CONTACT,
  roleContext: EMPTY_ROLE_CONTEXT,
  currentSystems: EMPTY_CURRENT_SYSTEMS,
  primaryObjectOfWork: [],
};

// ───────────────────────── Factory Functions ─────────────────────────

export function createEmptyProgramSubSections(): ProgramSubSections {
  return {
    scoping: {
      anticipatedPrograms: '',
      automatedReporting: '',
      reportCount: '',
      automatedReportingFile: [],
      programForms: '',
      formCount: '',
      automatedFormFile: [],
      consolidate: '',
      dataLocations: [],
      numberOfProperties: '',
      approxParticipants: '',
      approxDocumentsFiles: '',
      dataQuality: '',
    },
    currentSystems: { systems: [] },
    structure: { programTypes: {}, programTracking: {}, programTiedTo: {} },
    painPoints: { ...EMPTY_PROGRAM_PAIN_POINTS },
  };
}

export function createEmptyProjectsSubSections(): ProjectsSubSections {
  return {
    scoping: {
      anticipatedProjects: '',
      automatedReporting: '',
      reportCount: '',
      automatedReportingFile: [],
      programForms: '',
      formCount: '',
      automatedFormFile: [],
      consolidate: '',
      dataLocations: [],
      numberOfProperties: '',
      approxParticipants: '',
      approxDocumentsFiles: '',
      dataQuality: '',
    },
    currentSystems: { systems: [] },
    structure: { ...EMPTY_PROJECTS_STRUCTURE_DATA },
    painPoints: { ...EMPTY_PROJECTS_PAIN_POINTS },
  };
}

export function createEmptyPropertiesSubSection(): PropertiesSubSections {
  return {
    tracking: {
      propertyType: {},
      propertyTracking: {},
      propertyUpdates: {},
      visibilityGaps: '',
    },
    operationalSystems: { systems: [] },
    legacyConsolidation: {
      consolidate: '',
      propertyDataLive: {},
      numberOfProperties: '',
      approxParticipants: '',
      approxDocumentsFiles: '',
      dataQuality: '',
    },
    needs: {
      automatedReporting: '',
      reportCount: '',
      automatedReportingFile: [],
      programForms: '',
      formCount: '',
      automatedFormFile: [],
    },
    painPoints: { ...EMPTY_PROPERTIES_PAIN_POINTS },
  };
}
